/**
 * Discount & Tax Calc (owner, 18 Oct 2026: "حسب ايجار ما بيحسب"): Ejar's own bill, read off 2,485 contracts opened in
 * 2026 (live contract_money_reads.calc, every one matched to the fils). Pure rules, tested alone.
 *
 * - Offers (membership Yellow 10 / Silver 15 / Gold 20 / Platinum 25, online payment 10, new customer 5, Summer,
 *   corporate...) each compound; staff hand discounts add up among themselves, then compound with the offers:
 *   combined % = 1 - (1 - sum(staff %)) x product(1 - offer %), kept by Ejar to 5 decimals.
 * - Discount = total x combined %, VAT = 5 % of (total - discount), each to 2 decimals; net = total - discount + VAT.
 * - Ejar rounds half to even (.NET's default): 130 x 19.25 % = 25.025 -> 25.02.
 * - Add-ons are lines of their own (Shield Plus, Unlimited Salik, additional driver, baby seat, parking): price x the
 *   days, 5 % VAT each; the parking is ONE fixed fee, airport contracts only (owner, 18 Oct 2026: "على طول مرة واحده
 *   فى المطار ومفيش للباقي"); only the baby seat takes a discount - the membership's; a staff
 *   discount is typed on Trip Days only. The contract's net = the sum of its lines' nets (94 of 94 such contracts).
 * - The prices come from Ejar (engine api/price_quote.py): the car's rent for the days, each add-on's price and the
 *   customer's membership by identity - no membership = a new customer (owner, 18 Oct 2026).
 */

export const VAT_PCT = 5;

/** Ejar's membership levels and their Trip Days % (Offer/GetBenefits, "Internal Membership offer <level>"). */
export const MEMBERSHIP_PCT = { Yellow: 10, Silver: 15, Gold: 20, Platinum: 25 };
export const NEW_CUSTOMER_PCT = 5;

/** The most decimals Ejar keeps on a discount % (staff discounts typed with 5 decimals are kept whole). */
export const PCT_DECIMALS = 5;

/** Round half to even at `dp` decimals, as Ejar does; float noise (25.025 held as 25.02499...) is cleared first. */
export const roundEven = (value, dp = 2) => {
    const x = Number(value);
    if (!Number.isFinite(x)) return NaN;
    if (x < 0) return -roundEven(-x, dp);
    const f = 10 ** dp;
    const scaled = Number((x * f).toFixed(6));
    const whole = Math.floor(scaled);
    const frac = Number((scaled - whole).toFixed(6));
    if (frac === 0.5) return (whole % 2 === 0 ? whole : whole + 1) / f;
    return Math.round(scaled) / f;
};

const nums = (list) => (list || []).map(Number).filter((n) => Number.isFinite(n) && n !== 0);

/** "20, 15" / "20 15" -> [20, 15]: discounts as typed. */
export const parsePctList = (text) => String(text || '')
    .split(/[\s,;+]+/)
    .map((s) => s.replace('%', '').trim())
    .filter(Boolean)
    .map(Number)
    .filter((n) => Number.isFinite(n));

/** Ejar's combined discount % from its offers and staff discounts (lists of %), to 5 decimals. */
export const combinedPct = (offers = [], staff = []) => {
    const staffSum = nums(staff).reduce((a, b) => a + b, 0);
    const kept = nums(offers).reduce((p, o) => p * (1 - o / 100), 1 - staffSum / 100);
    return roundEven((1 - kept) * 100, PCT_DECIMALS);
};

/**
 * The customer's own offer from Ejar's answer (`customer`: { found, level, newCustomer } or null when no identity was
 * typed): the membership's %, else New customer 5 % - no membership is a new customer (owner, 18 Oct 2026).
 * -> { label, pct, membershipPct }.
 */
export const customerOffer = (customer) => {
    // Ejar did not answer for the membership: nothing is guessed, the user is told (engine price_quote.quote)
    if (customer && customer.error) return { label: 'Membership not read', pct: 0, membershipPct: 0, unread: true };
    const level = customer && customer.level;
    if (level && MEMBERSHIP_PCT[level]) {
        return { label: `${level} member`, pct: MEMBERSHIP_PCT[level], membershipPct: MEMBERSHIP_PCT[level] };
    }
    return { label: 'New customer', pct: NEW_CUSTOMER_PCT, membershipPct: 0 };
};

/** One line of Ejar's calculation: `unit` x `qty` less `pct` % discount, plus 5 % VAT. */
export const ejarLine = (unit, qty, pct = 0) => {
    const total = roundEven(Number(unit) * Number(qty), 2);
    const discount = roundEven(total * pct / 100, 2);
    const afterDiscount = roundEven(total - discount, 2);
    const vat = roundEven(afterDiscount * VAT_PCT / 100, 2);
    return { total, pct, discount, afterDiscount, vat, net: roundEven(afterDiscount + vat, 2) };
};

/**
 * Ejar's Trip Days bill: `rate` (daily rate before VAT) x `days`, the offers and staff discounts (lists of %).
 * -> { total, pct, discount, afterDiscount, vat, net } or null when the rate / days are not usable.
 */
export const ejarBill = ({ rate, days, offers = [], staff = [] }) => {
    const r = Number(rate);
    const d = Number(days);
    if (!(r > 0) || !(d > 0)) return null;
    return ejarLine(r, d, combinedPct(offers, staff));
};

/** The add-ons the calculator offers, in Ejar's order on a bill; `perDay` false = once (the airport parking). */
export const ADDONS = [
    { key: 'shieldPlus', label: 'Yelo Shield Plus', perDay: true },
    { key: 'unlimitedSalik', label: 'Unlimited Salik', perDay: true },
    { key: 'extraDriver', label: 'Additional driver', perDay: true },
    { key: 'babySeat', label: 'Baby seat', perDay: true },
    { key: 'parking', label: 'Airport parking', perDay: false },
];

/**
 * The add-on lines: `chosen` { shieldPlus: true, ..., parking: true }, `prices` { key: price before VAT } (Ejar's,
 * or typed where Ejar has none). A per-day add-on runs the contract's days; the airport parking is once. Only the baby seat
 * takes a discount: the membership's % (`membershipPct`), as Ejar gives it.
 */
export const addonLines = (days, chosen = {}, prices = {}, membershipPct = 0) => {
    const d = Number(days);
    if (!(d > 0)) return [];
    const lines = [];
    ADDONS.forEach(({ key, label, perDay }) => {
        const unit = Number(prices[key]);
        const qty = chosen[key] ? (perDay ? d : 1) : 0;
        if (!(unit > 0) || !(qty > 0)) return;
        const pct = key === 'babySeat' && Number(membershipPct) > 0 ? Number(membershipPct) : 0;
        lines.push({ key, label, qty, unit, ...ejarLine(unit, qty, pct) });
    });
    return lines;
};

const sumOf = (lines, key) => roundEven(lines.reduce((a, l) => a + l[key], 0), 2);

/**
 * The whole contract as Ejar bills it: Trip Days + the add-on lines -> { lines, total, discount, vat, net } (sums
 * of the lines), or null when the rate / days are not usable.
 */
export const contractBill = ({ rate, days, offers = [], staff = [], membershipPct = 0, chosen = {}, prices = {} }) => {
    const trip = ejarBill({ rate, days, offers, staff });
    if (!trip) return null;
    const lines = [
        { key: 'trip', label: 'Trip Days', qty: Number(days), unit: Number(rate), ...trip },
        ...addonLines(days, chosen, prices, membershipPct),
    ];
    return { lines, total: sumOf(lines, 'total'), discount: sumOf(lines, 'discount'), vat: sumOf(lines, 'vat'), net: sumOf(lines, 'net') };
};

/**
 * The extra (staff) discount % to type on Ejar so the whole contract comes to `target` (incl. VAT) - the amount
 * taken from the customer (owner, 18 Oct 2026: "اخدت من عميل مبلغ وعايز اعرف احط ديسكونت كام بعد الديسكونت الخاص
 * بيه"). The offers stay (membership, new customer, the Discount box); the add-ons keep their price, since a staff
 * discount on Ejar is on the rent only. The fewest decimals that land exactly (Ejar keeps up to 5). Returns
 *   { kind: 'exact' | 'closest', staffPct, bill }   - bill = the whole contract with that extra discount
 *   { kind: 'above', bill }                          - the offers alone already bring it to bill.net or lower
 *   { kind: 'tooLow', bill }                         - below the add-ons: no rent discount under 100 % gets there
 *   null                                             - nothing usable typed
 */
export const extraDiscountFor = ({ rate, days, offers = [], membershipPct = 0, chosen = {}, prices = {}, target }) => {
    const want = roundEven(target, 2);
    const base = contractBill({ rate, days, offers, membershipPct, chosen, prices });
    if (!base || !(want > 0)) return null;
    if (want >= base.net) return want === base.net ? { kind: 'exact', staffPct: 0, bill: base } : { kind: 'above', bill: base };
    const addonsNet = roundEven(base.net - base.lines[0].net, 2);
    const tripWant = roundEven(want - addonsNet, 2);
    if (!(tripWant > 0)) return { kind: 'tooLow', bill: base };
    const kept = nums(offers).reduce((p, o) => p * (1 - o / 100), 1);
    const exact = (1 - (tripWant / (1 + VAT_PCT / 100)) / (base.lines[0].total * kept)) * 100;
    const whole = (pct) => contractBill({ rate, days, offers, staff: [pct], membershipPct, chosen, prices });
    let closest = null;
    for (let dp = 0; dp <= PCT_DECIMALS; dp += 1) {
        const step = 10 ** -dp;
        const mid = roundEven(exact, dp);
        const tries = [mid, roundEven(mid - step, dp), roundEven(mid + step, dp)]
            .filter((p) => p > 0 && p < 100)
            .sort((a, b) => Math.abs(a - exact) - Math.abs(b - exact));
        for (const p of tries) {
            const bill = whole(p);
            if (bill.net === want) return { kind: 'exact', staffPct: p, bill };
            const gap = Math.abs(bill.net - want);
            if (dp === PCT_DECIMALS && (!closest || gap < closest.gap)) closest = { staffPct: p, bill, gap };
        }
    }
    return closest ? { kind: 'closest', staffPct: closest.staffPct, bill: closest.bill } : { kind: 'tooLow', bill: base };
};

/** Tax card: an amount as the total incl. 5 % VAT -> before VAT, and as the price before VAT -> with VAT. */
export const vatBothWays = (amount) => {
    const a = Number(amount);
    if (!Number.isFinite(a) || String(amount).trim() === '') return null;
    const before = roundEven(a / (1 + VAT_PCT / 100), 2);
    const vat = roundEven(a * VAT_PCT / 100, 2);
    return { before, vatInside: roundEven(a - before, 2), vat, after: roundEven(a + vat, 2) };
};
